import { captureMessage } from '@sentry/nextjs'

import { handleOpenAppAuthModal } from '@/features/app-auth-modal/utils/open-modal'

import { open503Toast } from './open-503-toast'
import { openToManyRequestToast } from './open-to-many-request-toast'
import { SERVICE_URLS } from './select-api-service'
import { setUrlParams } from './set-url-params'
import { ServiceName, TimeoutError } from './types'

import { TIMEOUT } from '../_constants/fetch-config'

interface Props {
    endpoint: string
    serviceName: ServiceName
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OPTIONS'
    body?: object
    isBodyFormData?: boolean
    appendCurrentQueryStrings?: boolean
    openAuthModal?: boolean
}

export default async function fetchRequest({
    endpoint,
    serviceName,
    method = 'GET',
    body,
    isBodyFormData = false,
    appendCurrentQueryStrings = true,
    openAuthModal = true,
}: Props) {
    let url = `${SERVICE_URLS[serviceName]}${endpoint}`

    if (appendCurrentQueryStrings) {
        url = setUrlParams(url)
    }

    if (!SERVICE_URLS[serviceName]) {
        captureMessage(
            `fetch request error: ${JSON.stringify({ endpoint, serviceName, method, body, isBodyFormData, appendCurrentQueryStrings })}`
        )
        return false
    }

    const controller = new AbortController()
    const timeoutError = new TimeoutError()
    const timeoutId = setTimeout(() => {
        controller.abort(timeoutError)
    }, TIMEOUT)

    try {
        const response = await fetch(url, {
            method,
            headers: isBodyFormData
                ? {}
                : {
                      'Content-Type': 'application/json; charset=utf-8',
                  },
            ...(body ? { body: isBodyFormData ? (body as FormData) : JSON.stringify(body) } : {}),
            signal: controller.signal,
            cache: 'no-cache',
        })

        if (response.status === 401 && openAuthModal) {
            handleOpenAppAuthModal()

            // eslint-disable-next-line no-throw-literal
            throw 'unauthorized_user'
        }

        if (response.status === 429) {
            openToManyRequestToast()

            // eslint-disable-next-line no-throw-literal
            throw []
        }

        if (response.status === 503) {
            open503Toast()

            // eslint-disable-next-line no-throw-literal
            throw []
        }

        // handle no content
        if (response.status === 204) {
            return null
        } else {
            const result = await response.json()

            if (!response.ok) {
                // eslint-disable-next-line no-throw-literal
                throw [{ ...result?.errors?.[0], status: response.status }, result?.data]
            }

            return result?.data
        }
    } catch (error) {
        clearTimeout(timeoutId)

        if (error instanceof TimeoutError) {
            throw error
        }

        if (typeof error !== 'string') throw error

        throw new Error(typeof error === 'string' ? error : `Error fetching: ${error}`)
    } finally {
        clearTimeout(timeoutId)
    }
}
